import { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarX2, Copy, Download, Ellipsis, Upload } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { CampaignActionsSheet } from '@/components/campaigns/campaign-actions-sheet';
import { CampaignCard } from '@/components/campaigns/campaign-card';
import { CampaignStatusFilter } from '@/components/campaigns/campaign-status-filter';
import { CancelCampaignDialog } from '@/components/campaigns/cancel-campaign-dialog';
import { SwipeableRow } from '@/components/campaigns/swipeable-row';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Skeleton } from '@/components/ui/skeleton';
import type { CampaignDto } from '@/generated/korza-api-client/types.gen';
import {
  type CampaignsFilters,
  useInfiniteCampaigns,
} from '@/hooks/use-campaigns';
import { useCancelCampaign } from '@/hooks/use-cancel-campaign';
import { useTenant } from '@/hooks/use-tenant';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  canCancelCampaign,
  type CampaignStatusFilter as StatusFilter,
  groupCampaignsByMonth,
  statusFilterToApi,
} from '@/lib/campaign-list';
import { mobileLayer } from '@/lib/mobile-layer';
import { cn, getLuxonLocale } from '@/lib/utils';

const EMPTY: CampaignDto[] = [];
/** Width of each action revealed behind a dragged card. */
const SWIPE_ACTION_WIDTH = 80;
const swipeActionClassName =
  'flex h-full flex-1 flex-col items-center justify-center gap-1 text-xs font-medium outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring';

/**
 * Infinite list (pages of 50) behind the mobile Campanhas, filtered by the
 * status chip and the name search. The list and the header ⋯ (export) share
 * it through the query key.
 */
export function useMobileCampaigns(
  filter: StatusFilter,
  search: string,
  enabled = true
) {
  const filters = useMemo<CampaignsFilters>(
    () => ({
      status: statusFilterToApi(filter),
      search: search === '' ? undefined : search,
    }),
    [filter, search]
  );
  const query = useInfiniteCampaigns(filters, enabled);
  const campaigns = useMemo(
    () => query.data?.pages.flatMap((p) => p.data) ?? EMPTY,
    [query.data]
  );
  return { query, campaigns };
}

/**
 * Campanhas below 768px (PUSH-01 AC 1–4, 7): status chips, cards grouped by
 * month of `launchDateTime` in the API order (newest first), the next page
 * when the end of the list shows up, and an empty state with "Criar
 * campanha". Dragging a card to the left (one open at a time) or its ⋯
 * offers Duplicar and, while not sent, Cancelar with confirmation (PUSH-04
 * AC 1–4).
 */
export function CampaignsMobileList({
  filter,
  search,
  onFilterChange,
  currency,
  analyticsEnabled,
}: {
  filter: StatusFilter;
  search: string;
  onFilterChange(filter: StatusFilter): void;
  /** `undefined` while the store currency loads (money shows a skeleton). */
  currency: string | undefined;
  analyticsEnabled: boolean;
}) {
  const { LL, locale } = useI18nContext();
  const tenant = useTenant();
  const luxonLocale = getLuxonLocale(locale);
  const navigate = useNavigate();
  const { query, campaigns } = useMobileCampaigns(filter, search);
  const cancelCampaign = useCancelCampaign();
  const [openId, setOpenId] = useState<string | null>(null);
  const [sheet, setSheet] = useState<{
    campaign: CampaignDto | null;
    open: boolean;
  }>({ campaign: null, open: false });
  const sheetTriggerRef = useRef<HTMLElement | null>(null);
  const [cancelTarget, setCancelTarget] = useState<CampaignDto | null>(null);

  const duplicate = (campaign: CampaignDto) =>
    navigate(`/${tenant.slug}/campaigns/create?from=${campaign.id}`);
  const askToCancel = (campaign: CampaignDto) => {
    setOpenId(null);
    setCancelTarget(campaign);
  };
  const confirmCancel = () => {
    if (!cancelTarget) return;
    cancelCampaign.mutate(
      { campaignId: cancelTarget.id, campaignName: cancelTarget.name },
      { onSettled: () => setCancelTarget(null) }
    );
  };
  const groups = useMemo(
    () => groupCampaignsByMonth(campaigns, luxonLocale),
    [campaigns, luxonLocale]
  );

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || isFetchingNextPage) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void fetchNextPage();
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const body = () => {
    if (query.isPending) {
      return (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton className="h-32 w-full rounded-mobile-card" />
          <Skeleton className="h-32 w-full rounded-mobile-card" />
        </div>
      );
    }
    if (query.isError) {
      return (
        <p className="py-10 text-center text-sm text-muted-foreground">
          {LL.campaigns.failedToLoadCampaigns()}
        </p>
      );
    }
    if (campaigns.length === 0) {
      return (
        <div className="flex flex-col items-center gap-4 py-10 text-center">
          <p className="text-sm text-muted-foreground">
            {LL.campaigns.noCampaignsFound()}
          </p>
          <Button asChild className={mobileLayer.touch}>
            <Link to={`/${tenant.slug}/campaigns/create`}>
              {LL.campaigns.mobileList.createCampaign()}
            </Link>
          </Button>
        </div>
      );
    }
    return (
      <>
        {groups.map((group) => (
          <section key={group.key} className="flex flex-col gap-3">
            <h2 className="text-sm font-semibold text-muted-foreground first-letter:uppercase">
              {group.label}
            </h2>
            {group.campaigns.map((campaign) => {
              const cancellable = canCancelCampaign(campaign);
              return (
                <SwipeableRow
                  key={campaign.id}
                  open={openId === campaign.id}
                  onOpenChange={(open) =>
                    setOpenId((current) =>
                      open
                        ? campaign.id
                        : current === campaign.id
                          ? null
                          : current
                    )
                  }
                  actionsWidth={SWIPE_ACTION_WIDTH * (cancellable ? 2 : 1)}
                  actions={
                    <>
                      <button
                        type="button"
                        className={cn(
                          swipeActionClassName,
                          'bg-secondary text-secondary-foreground'
                        )}
                        onClick={() => duplicate(campaign)}
                      >
                        <Copy className="size-5" aria-hidden />
                        {LL.campaigns.actionsSheet.duplicate()}
                      </button>
                      {cancellable && (
                        <button
                          type="button"
                          className={cn(
                            swipeActionClassName,
                            'bg-destructive text-destructive-foreground'
                          )}
                          onClick={() => askToCancel(campaign)}
                        >
                          <CalendarX2 className="size-5" aria-hidden />
                          {LL.campaigns.actionsSheet.cancel()}
                        </button>
                      )}
                    </>
                  }
                >
                  <CampaignCard
                    campaign={campaign}
                    currency={currency}
                    analyticsEnabled={analyticsEnabled}
                    locale={luxonLocale}
                    onOpenActions={(trigger) => {
                      sheetTriggerRef.current = trigger;
                      setOpenId(null);
                      setSheet({ campaign, open: true });
                    }}
                  />
                </SwipeableRow>
              );
            })}
          </section>
        ))}
        <div ref={sentinelRef} aria-hidden className="h-px" />
        {isFetchingNextPage && (
          <Skeleton className="h-32 w-full rounded-mobile-card" />
        )}
      </>
    );
  };

  return (
    // Bottom padding keeps the last card clear of the "Nova campanha" FAB.
    <div className="flex flex-col gap-5 pb-24">
      <CampaignStatusFilter value={filter} onChange={onFilterChange} />
      {body()}
      <CampaignActionsSheet
        campaign={sheet.campaign}
        open={sheet.open}
        onOpenChange={(open) => setSheet((current) => ({ ...current, open }))}
        onDuplicate={duplicate}
        onCancel={askToCancel}
        returnFocusRef={sheetTriggerRef}
      />
      <CancelCampaignDialog
        open={cancelTarget !== null}
        onOpenChange={(open) => {
          if (!open && !cancelCampaign.isPending) setCancelTarget(null);
        }}
        campaignName={cancelTarget?.name ?? ''}
        isPending={cancelCampaign.isPending}
        onConfirm={confirmCancel}
      />
    </div>
  );
}

/**
 * ⋯ of the mobile header (PUSH-01 AC 5): "Importar CSV" and "Exportar (N
 * carregadas)", which exports the campaigns loaded so far.
 */
export function CampaignsMobileHeaderMenu({
  loadedCount,
  onImport,
  onExport,
}: {
  loadedCount: number;
  onImport(): void;
  onExport(): void;
}) {
  const { LL } = useI18nContext();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const rowClassName =
    'flex min-h-touch w-full items-center gap-3 rounded-lg px-3 text-left text-sm font-medium outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';

  const pick = (action: () => void) => () => {
    setOpen(false);
    action();
  };

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="ghost"
        size="icon"
        aria-label={LL.campaigns.mobileList.moreActions()}
        className={mobileLayer.touch}
        onClick={() => setOpen(true)}
      >
        <Ellipsis className="size-5" aria-hidden />
      </Button>
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent returnFocusRef={triggerRef}>
          <DrawerHeader className="pb-2 text-left">
            <DrawerTitle>{LL.campaigns.title()}</DrawerTitle>
            <DrawerDescription className="sr-only">
              {LL.campaigns.mobileList.menuDescription()}
            </DrawerDescription>
          </DrawerHeader>
          <div className="flex flex-col gap-1 px-2 pb-4">
            <button
              type="button"
              className={rowClassName}
              onClick={pick(onImport)}
            >
              <Upload className="size-4" aria-hidden />
              {LL.campaigns.importCsvButton()}
            </button>
            <button
              type="button"
              className={rowClassName}
              disabled={loadedCount === 0}
              onClick={pick(onExport)}
            >
              <Download className="size-4" aria-hidden />
              {LL.campaigns.mobileList.exportLoaded({ count: loadedCount })}
            </button>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
